import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

/**
 * A generic multi-select dropdown with chips.
 *
 * Props:
 *   options: [{ value, label, hint? }]
 *   values: string[]
 *   onChange: (string[]) => void
 *   placeholder: string
 *   allOption: { label, checked, onToggle } — optional top-level "select all" toggle
 *     (e.g. "All departments"). When checked, individual options are grayed out.
 *   testId: for the trigger button
 *   emptyLabel: what to show when nothing is selected AND no allOption is checked
 */
export const MultiSelect = ({
  options = [],
  values = [],
  onChange,
  placeholder = 'Select…',
  allOption = null,
  testId,
  emptyLabel,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const byValue = useMemo(() => {
    const m = {};
    options.forEach((o) => { m[o.value] = o; });
    return m;
  }, [options]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  const chips = allOption?.checked
    ? [{ label: allOption.label, isAll: true }]
    : values.map((v) => ({ value: v, label: byValue[v]?.label || v }));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="w-full min-h-[42px] border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center gap-1.5 flex-wrap"
      >
        {chips.length === 0 && (
          <span className="text-[#0b1a2e]/40">{emptyLabel || placeholder}</span>
        )}
        {chips.map((c) => (
          <span
            key={c.value || 'all'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.isAll ? 'bg-[#0f5c3d] text-white' : 'bg-[#e2ede6] text-[#0b1a2e]'}`}
          >
            {c.label}
            {!c.isAll && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); toggle(c.value); }}
                className="hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </span>
            )}
          </span>
        ))}
        <ChevronDown className="w-4 h-4 text-[#0b1a2e]/40 ml-auto" />
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-black/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {allOption && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); allOption.onToggle(!allOption.checked); }}
              data-testid={testId ? `${testId}-all` : undefined}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-b border-black/5 hover:bg-[#f7fbf8] text-left"
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${allOption.checked ? 'bg-[#0f5c3d] border-[#0f5c3d]' : 'border-[#0f5c3d]/30'}`}>
                {allOption.checked && <Check className="w-3 h-3 text-white" />}
              </span>
              <span className="font-medium text-[#0f5c3d]">{allOption.label}</span>
            </button>
          )}
          {options.length === 0 && (
            <div className="px-3 py-3 text-xs text-[#0b1a2e]/50">No options available</div>
          )}
          {options.map((o) => {
            const checked = values.includes(o.value);
            const disabled = allOption?.checked;
            return (
              <button
                key={o.value}
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!disabled) toggle(o.value); }}
                disabled={disabled}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#f7fbf8] text-left ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-[#0f5c3d] border-[#0f5c3d]' : 'border-[#0f5c3d]/30'}`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="text-[#0b1a2e]">{o.label}</span>
                {o.hint && <span className="ml-auto text-[10px] text-[#0b1a2e]/50">{o.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
